import {memo, useEffect, useMemo, useRef, useState} from "react";
import {FlowMode, SavingState} from "@/types/node";
import {Button} from "@/components/ui/Button";
import {
  Braces,
  History,
  Redo2,
  RefreshCw,
  Search,
  Undo2,
  Workflow,
} from "lucide-react";
import {TooltipContent, TooltipTrigger, Tooltip} from "@/components/ui/tooltip";
import {useSave} from "@/contexts/SaveContext";
import {useSubflows} from "@/contexts/SubflowsContext";
import {ResetModal} from "./Modals/ResetModal";
import {restoreNodeSettings} from "@/utils/index";
import {createDefaultNodes} from "@/utils/defaultFlowData";
import {useDrawer} from "@/contexts/DrawerContext";
import {AnimatePresence, motion} from "framer-motion";
import {cn} from "@/lib/utils";
import {DRAWER_IDS} from "@/components/Drawers";

const Toolbar = memo(function Toolbar({
  undoRedoDetails,
}: {
  undoRedoDetails: {
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
  };
}) {
  const drawersList = useMemo(() => {
    return [
      DRAWER_IDS.SEARCH,
      DRAWER_IDS.SECTIONS,
      DRAWER_IDS.VARIABLES,
      DRAWER_IDS.HISTORY,
    ];
  }, []);
  const {flowMode, handleSave, savingState} = useSave();
  const {mainFlow, subflows, selectedSubflow, updateSubflow} = useSubflows();
  const {openDrawer, drawerRefs, isDrawerOpen, closeDrawer} = useDrawer();
  const {undo, redo, canUndo, canRedo} = undoRedoDetails;
  const [showResetModal, setShowResetModal] = useState(false);
  const [toolbarExpanded, setToolbarExpanded] = useState(false);
  const activeDrawer = useRef<string | null>(null);

  // Keep refs updated with latest values for reset operation
  const mainFlowRef = useRef(mainFlow);
  const subflowsRef = useRef(subflows);
  const selectedSubflowRef = useRef(selectedSubflow);

  useEffect(() => {
    mainFlowRef.current = mainFlow;
  }, [mainFlow]);

  useEffect(() => {
    subflowsRef.current = subflows;
  }, [subflows]);

  useEffect(() => {
    selectedSubflowRef.current = selectedSubflow;
  }, [selectedSubflow]);

  const activeClassnames =
    "bg-primary text-primary-accent bg-opacity-10 hover:bg-primary hover:text-primary-accent hover:bg-opacity-20";

  const drawerWidth = useMemo(
    () =>
      activeDrawer.current
        ? (drawerRefs.get(activeDrawer.current)?.current?.clientWidth ?? 0)
        : 0,
    [activeDrawer.current, drawerRefs],
  );
  const isLiveMode = flowMode === FlowMode.LIVE;

  const handleReset = () => {
    // Use refs to get the latest values (important to avoid stale data)
    const latestMainFlow = mainFlowRef.current;
    const latestSubflows = subflowsRef.current;

    // Copy live data to test for ALL subflows and main flow
    // Update main flow: copy live to test
    // Increment version to force canvas rerender
    updateSubflow("main", {
      flowData: {
        ...latestMainFlow.flowData,
        test: {
          version: (latestMainFlow.flowData.live.version || 0) + 1,
          nodes: latestMainFlow.flowData.live.nodes,
          edges: latestMainFlow.flowData.live.edges,
        },
      },
    });

    // Update all subflows: copy live to test
    latestSubflows.forEach(subflow => {
      updateSubflow(subflow.id, {
        flowData: {
          ...subflow.flowData,
          test: {
            version: (subflow.flowData.live.version || 0) + 1,
            nodes: subflow.flowData.live.nodes,
            edges: subflow.flowData.live.edges,
          },
        },
      });
    });

    handleSave();
  };

  const toggleDrawer = (id: string) => {
    const isEligibleDrawer = drawersList.includes(id);
    if (!isEligibleDrawer) return;

    if (isDrawerOpen(id)) {
      setToolbarExpanded(false);
      activeDrawer.current = null;
      closeDrawer(id);
    } else {
      setToolbarExpanded(true);
      activeDrawer.current = id;
      openDrawer(id);
    }
  };

  const eligibleDrawers = useMemo(
    () => drawersList.filter(drawer => drawerRefs.has(drawer)),
    [drawerRefs, drawersList],
  );
  useEffect(() => {
    if (eligibleDrawers.length === 0) {
      activeDrawer.current = null;
      setToolbarExpanded(false);
    } else {
      activeDrawer.current = eligibleDrawers[0] || null;
      setToolbarExpanded(true);
    }
  }, [eligibleDrawers]);

  return (
    <AnimatePresence>
      <motion.div
        initial={false}
        animate={{
          x: toolbarExpanded ? drawerWidth + 24 : 16,
          y: 120,
          opacity: 1,
        }}
        transition={{duration: 0.2, ease: "easeInOut"}}
        className="absolute z-50"
      >
        <div
          className="z-50 flex flex-col gap-1.5"
          style={
            savingState === SavingState.LOADING
              ? {
                  opacity: 0.5,
                  pointerEvents: "none",
                  cursor: "wait",
                }
              : {}
          }
        >
          <div className="flex flex-col gap-1 p-1 rounded-xl border border-divider bg-white">
            <Tooltip delayDuration={0} disableHoverableContent={true}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "[&_svg]:size-5",
                    activeDrawer.current === DRAWER_IDS.SEARCH &&
                      activeClassnames,
                  )}
                  onClick={() => toggleDrawer(DRAWER_IDS.SEARCH)}
                >
                  <Search />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10}>
                Search
              </TooltipContent>
            </Tooltip>
          </div>
          {/* <div className="flex flex-col gap-1 p-1 rounded-xl border border-divider bg-white">
            <Tooltip delayDuration={0} disableHoverableContent={true}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "[&_svg]:size-5",
                    activeDrawer.current === DRAWER_IDS.SECTIONS &&
                      activeClassnames,
                  )}
                  onClick={() => toggleDrawer(DRAWER_IDS.SECTIONS)}
                >
                  <Workflow />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10}>
                Flow Sections
              </TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={0} disableHoverableContent={true}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "[&_svg]:size-5",
                    activeDrawer.current === DRAWER_IDS.VARIABLES &&
                      activeClassnames,
                  )}
                  onClick={() => toggleDrawer(DRAWER_IDS.VARIABLES)}
                >
                  <Braces />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10}>
                Variables
              </TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={0} disableHoverableContent={true}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "[&_svg]:size-5",
                    activeDrawer.current === DRAWER_IDS.HISTORY &&
                      activeClassnames,
                  )}
                  onClick={() => toggleDrawer(DRAWER_IDS.HISTORY)}
                >
                  <History />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10}>
                History
              </TooltipContent>
            </Tooltip>
          </div> */}
          <div className="flex flex-col gap-1 p-1 rounded-xl border border-divider bg-white">
            {!isLiveMode && (
              <Tooltip delayDuration={0} disableHoverableContent={true}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="[&_svg]:size-5"
                    onClick={() => setShowResetModal(true)}
                  >
                    <RefreshCw />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={10}>
                  Reset
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip delayDuration={0} disableHoverableContent={true}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="[&_svg]:size-5"
                  onClick={() => {
                    handleSave();
                    undo();
                  }}
                  disabled={!canUndo || isLiveMode}
                >
                  <Undo2 />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10}>
                Undo
              </TooltipContent>
            </Tooltip>

            <Tooltip delayDuration={0} disableHoverableContent={true}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="[&_svg]:size-5"
                  onClick={() => {
                    handleSave();
                    redo();
                  }}
                  disabled={!canRedo || isLiveMode}
                >
                  <Redo2 />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10}>
                Redo
              </TooltipContent>
            </Tooltip>
          </div>
          <ResetModal
            onReset={handleReset}
            showModal={showResetModal}
            setShowModal={setShowResetModal}
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );
});

export default Toolbar;
